import { useUpdateProductQuery } from 'lib/react-query/Queries/useUpdateProductQuery';
import { TDropDownItem } from 'lib/types/dropdown';
import { InclusionFormat, SelectedRoom } from 'lib/types/trip';
import { useEffect, useRef, useState } from 'react';
import { AppToastService } from '../../components/Toast/AppToastService';
import { SelectionAvailability } from 'lib/types/availability';
import { FlexibleDepartureDecision } from 'lib/types/product';
import { useCustomNavigate } from './useCustomNavigate';
import { stagePathConfig } from 'data/stage';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { setTravellerNumber } from 'state/slices/product';
import { useTenant } from './useTenant';
import { useQueryClient } from '@tanstack/react-query';
import { useCreateTraveller } from 'lib/react-query/Queries/useCreateTraveller';
import { GetTravellerResult } from 'lib/types/create-traveller';
import { checkTravellerData } from 'lib/helper/checkTravellerData';
import { getDateOfBirthFromUnix } from 'lib/helper/traveller/date-of-birth';
import { sessionIdService } from 'services/session-id/session-id.service';

export type RoomValidationKind = 'unassigned' | 'wrongRoom' | 'ok';

type Props = {
  travellerDropdown: TDropDownItem | null;
  selectedRooms: SelectedRoom[];
  isGotToGo: boolean;
};

const useSubmitRoom = ({
  travellerDropdown,
  selectedRooms,
  isGotToGo,
}: Props) => {
  const [hasError, setHasError] = useState(true);
  const [validationMessage, setValidationMessage] = useState('');
  const [validationKind, setValidationKind] =
    useState<RoomValidationKind>('unassigned');
  const navigate = useCustomNavigate();
  const { isLoading, isError, data, submitUpdateProduct } =
    useUpdateProductQuery();
  const availability = useAppSelector((state) => state.product.availability);
  const flexibleDeparture = useAppSelector(
    (state) => state.product.flexibleDeparture
  );
  const flexibleDepartureDecision = useAppSelector(
    (state) => state.product.flexibleDepartureDecision
  );
  const selectedDuration = useAppSelector((state) => state.product.duration);
  const duration_id = selectedDuration?.id;
  const dispatch = useAppDispatch();
  const { isDA } = useTenant();
  const queryClient = useQueryClient();

  // DealsAway saves rooms with a second setup call (Traveller came first).
  const {
    isLoading: isSetupLoading,
    isError: isSetupError,
    isSuccess: isSetupSuccess,
    data: setupData,
    mutate: submitSetup,
  } = useCreateTraveller({ redirectToTravellerOnError: false });
  const isSetupSubmitting = useRef(false);

  const continueHandler = () => {
    navigate(stagePathConfig.traveller);
  };

  const submitDealsAwayRooms = (
    travellersNumber: number,
    isFlexible?: boolean,
    departureDate?: SelectionAvailability,
    flexibleDecision?: FlexibleDepartureDecision | null
  ) => {
    if (isSetupSubmitting.current) return;

    const traveller = queryClient.getQueryData<GetTravellerResult>([
      'get-traveller-info',
      sessionIdService.get(),
    ])?.result;

    // Setup needs the lead captured on Traveller; send them back if missing.
    if (!traveller || !checkTravellerData(traveller)) {
      AppToastService.error('Please add your traveller details to continue.');
      navigate(stagePathConfig.traveller);
      return;
    }

    isSetupSubmitting.current = true;
    submitSetup(
      {
        email: traveller.email!,
        firstName: traveller.first_name!,
        lastName: traveller.last_name!,
        phonePrefix: traveller.phone_code,
        phoneNumber: traveller.phone_number,
        dateOfBirth: getDateOfBirthFromUnix(traveller.date_of_birth!),
        flexibleDeparture: isFlexible,
        flexibleDepartureDecision: flexibleDecision,
        selectedAvailability: departureDate,
        selectedRooms,
        travellersNumber,
      },
      {
        onSuccess: () => {
          dispatch(setTravellerNumber(travellersNumber));
        },
        onSettled: () => {
          isSetupSubmitting.current = false;
        },
      }
    );
  };

  const submitHandler = (
    isFlexible?: boolean,
    departureDate?: SelectionAvailability,
    flexibleDecision?: FlexibleDepartureDecision | null
  ) => {
    if (!hasError && isDA) {
      submitDealsAwayRooms(
        travellerDropdown?.value ? parseInt(travellerDropdown.value) : 0,
        isFlexible,
        departureDate,
        flexibleDecision
      );
      return;
    }

    if (!hasError) {
      const inclusionFormat: InclusionFormat[] = selectedRooms.map((item) => ({
        type: 'room',
        data: { id: item.id, travellers_number: item?.count },
      }));

      submitUpdateProduct({
        availability: departureDate ?? availability,
        duration_id,
        rooms: inclusionFormat,
        travellersNumber: travellerDropdown?.value
          ? parseInt(travellerDropdown.value)
          : 0,
        is_departure_flexible: isFlexible ?? flexibleDeparture,
        flexible_departure_decision:
          flexibleDecision ?? flexibleDepartureDecision,
      });

      travellerDropdown &&
        dispatch(setTravellerNumber(parseInt(travellerDropdown?.value)));
    }
  };

  useEffect(() => {
    if (travellerDropdown) {
      const selectedRoomsCount = selectedRooms
        .map((item) => item?.count)
        .reduce((partialSum, a) => partialSum + a, 0);
      const differenceCount =
        parseInt(travellerDropdown?.value) - selectedRoomsCount;
      if (differenceCount !== 0) {
        setHasError(true);
        setValidationKind('unassigned');
        setValidationMessage(
          isDA
            ? `Assign ${Math.max(differenceCount, 1)} traveller${
                differenceCount <= 1 ? '' : 's'
              } to accommodation to continue.`
            : `
Please select ${differenceCount} more traveller${
                differenceCount > 1 ? 's' : ''
              }`
        );
      } else if (
        selectedRooms
          .map((item) => item.hasError)
          .reduce((partialSum, a) => partialSum * (a === true ? 0 : 1), 1) === 0
      ) {
        setHasError(true);
        setValidationKind('wrongRoom');
        setValidationMessage(
          `Please select correct accommodation to continue.`
        );
      } else {
        setHasError(false);
        setValidationKind('ok');
        setValidationMessage(`You're good to go`);
      }
    }
  }, [travellerDropdown, selectedRooms, isDA]);

  useEffect(() => {
    if (isDA) return;
    if (isLoading) return;

    if (data && data.success && !isError && data.result.updated) {
      if (!isGotToGo) {
        continueHandler();
      }
    } else if (isError) {
      AppToastService.error('Oops! Something went wrong!');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, data, isError]);

  // DealsAway: Payment comes after Rooms, once setup has saved the rooms.
  // Setup errors are already shown by useCreateTraveller.
  useEffect(() => {
    if (!isDA || isSetupLoading) return;

    if (isSetupSuccess && setupData?.success && !isGotToGo) {
      navigate(stagePathConfig.payment);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSetupLoading, isSetupSuccess, setupData]);

  const isSuccess = isDA
    ? isSetupSuccess && Boolean(setupData?.success)
    : Boolean(data && data.success && !isError && data.result.updated);

  return {
    isLoading: isDA ? isSetupLoading : isLoading,
    hasError,
    validationMessage,
    validationKind,
    submitHandler,
    data,
    isError: isDA ? isSetupError : isError,
    isSuccess,
  };
};
export { useSubmitRoom };
